import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { StanFilterValue } from '../types';
import { TEXT_OP_LABELS } from '../utils/filterOperators';
import { isTextOp, TextOp } from '../utils/filterTypes';

type UseTextFilterArgs = {
    filterItem: any;
    onApply: (filters: StanFilterValue[]) => void;
    onClearFilter?: (id: string) => void;
    disabled?: boolean;
    closeMenu: () => void;
};

export const useTextFilter = ({ filterItem, onApply, onClearFilter, closeMenu }: UseTextFilterArgs) => {
    const [value, setValue] = useState('');
    const [operator, setOperator] = useState<TextOp>('li');
    const [appliedValue, setAppliedValue] = useState('');

    const summary = useMemo(() => (appliedValue ? `${appliedValue}` : ''), [appliedValue]);
    const operatorOptions = Object.entries(TEXT_OP_LABELS).map(([value, label]) => ({ label, value }));

    const resetState = useCallback(() => {
        setValue('');
        setOperator('li');
        setAppliedValue('');
    }, []);

    const clearFilters = useCallback(
        (event?: MouseEvent) => {
            event?.stopPropagation();
            resetState();
            onApply([]);
            onClearFilter?.(filterItem.id);
            closeMenu();
        },
        [closeMenu, filterItem.id, onApply, onClearFilter, resetState]
    );

    // Initialize state from filterItem.initFilterValue
    useEffect(() => {
        const init = filterItem.initFilterValue;
        if (init === null) {
            resetState();
            closeMenu();
        } else if (init) {
            setValue(init.value || '');
            setOperator(init.operatorCode === 'li' ? 'li' : 'eq');
            setAppliedValue(init.value || '');
        }
    }, [filterItem.initFilterValue, resetState, closeMenu]);

    const apply = useCallback(() => {
        if (!value) {
            onApply([]);
            setAppliedValue('');
            closeMenu();
            return;
        }
        const out: StanFilterValue[] = [
            {
                id: filterItem.id,
                operator,
                value,
                type: 'text',
            },
        ];
        onApply(out);
        setAppliedValue(value);
        closeMenu();
    }, [closeMenu, filterItem.id, onApply, operator, value]);

    const handleOperatorSelect = useCallback((value: string) => {
        if (isTextOp(value)) {
            setOperator(value);
        }
    }, []);

    const currentOperator = operatorOptions.find(opt => opt.value === operator) ?? null;

    /**
     * Handles input change with validation
     * Extracted for better separation of concerns
     */
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    };

    /**
     * Handles operator selection with type safety
     * Extracted for better separation of concerns
     */
    const handleOperatorChange = (value: string) => {
        handleOperatorSelect(value);
    };

    const hasSummary = summary.length > 0;

    return {
        summary,
        hasSummary,
        operatorOptions,
        currentOperator,
        value,
        setValue,
        handleOperatorSelect,
        apply,
        clearFilters,
        // New functionality extracted from component
        handleInputChange,
        handleOperatorChange,
    };
};
